import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  ArrowRight,
  Check,
  Mail,
  Wallet,
  Users,
  Target,
  Repeat,
  FolderTree,
  Archive,
  CreditCard,
} from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import SEOHead from '../components/common/SEOHead';
import { subscribePrelaunch } from '../services/auth';
import { getErrorMessage } from '../services/api';
import PhoneMockup from '../components/common/PhoneMockup';
import LaptopMockup from '../components/common/LaptopMockup';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import { useRegistrationEnabled } from '../hooks/usePublicConfig';

const featureConfigs = [
  { icon: LayoutDashboard, key: 'dashboard', href: '/fonctionnalites/dashboard' },
  { icon: Wallet, key: 'operations', href: '/fonctionnalites/operations' },
  { icon: Users, key: 'sharing', href: '/fonctionnalites/partage' },
  { icon: Target, key: 'budgets', href: '/fonctionnalites/budgets' },
];

const highlightConfigs = [
  { icon: CreditCard, key: 'multiAccount' },
  { icon: Repeat, key: 'recurring' },
  { icon: Archive, key: 'archive' },
  { icon: Users, key: 'compatible' },
  { icon: FolderTree, key: 'categories' },
];

function RotatingWord() {
  const { t } = useTranslation('landing');
  return (
    <span className="inline-block overflow-hidden align-bottom relative" style={{ height: '1.2em' }}>
      <span className="rotating-words-track">
        <span className="rotating-word text-gradient">{t('rotatingWords.couple')}</span>
        <span className="rotating-word text-gradient">{t('rotatingWords.colocation')}</span>
        <span className="rotating-word text-gradient">{t('rotatingWords.couple')}</span>
      </span>
    </span>
  );
}

export default function Landing() {
  const { t } = useTranslation('landing');
  const { t: tc } = useTranslation('common');
  const registrationEnabled = useRegistrationEnabled();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const features = featureConfigs.map(fc => ({
    ...fc,
    title: t(`features.${fc.key}.title`),
    description: t(`features.${fc.key}.description`),
  }));

  const highlights = highlightConfigs.map(hc => ({
    ...hc,
    text: t(`highlights.${hc.key}`),
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await subscribePrelaunch(email);
      setIsSuccess(true);
      setEmail('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pub-page min-h-screen">
      <SEOHead
        title="Splinity - Budget équitable pour couples et colocations"
        description="Gérez vos dépenses de couple ou colocation équitablement. Répartition proportionnelle aux revenus, budgets prévisionnels, multi-comptes, opérations récurrentes. Gratuit."
        path="/"
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="pub-hero pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="pub-title text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              {t('hero.title')}{' '}
              <span className="text-gradient">{t('hero.titleHighlight')}</span>{' '}
              <RotatingWord />
            </h1>
            <p className="pub-muted text-xl mb-8">
              {t('hero.subtitle')}
            </p>

            {/* CTA conditionnel */}
            <div className="max-w-md mx-auto">
              {registrationEnabled === true ? (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/register">
                    <Button
                      variant="primary"
                      size="lg"
                      rightIcon={<ArrowRight className="w-4 h-4" />}
                    >
                      {t('hero.createAccount')}
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="outline" size="lg">
                      {t('hero.signIn')}
                    </Button>
                  </Link>
                </div>
              ) : registrationEnabled === false ? (
                <>
                  {isSuccess ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 animate-fade-in">
                      <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
                        <Check className="w-6 h-6 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-green-800 mb-2">
                        {t('prelaunch.thankYou')}
                      </h3>
                      <p className="text-green-700">
                        {t('prelaunch.contactSoon')}
                      </p>
                    </div>
                  ) : (
                    <div className="pub-card p-6">
                      <div className="pub-ico flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full">
                        <Mail className="w-6 h-6" />
                      </div>
                      <h3 className="pub-title text-lg font-semibold mb-2">
                        {t('prelaunch.title')}
                      </h3>
                      <p className="pub-muted text-sm mb-4">
                        {t('prelaunch.subtitle')}
                      </p>
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <Input
                          type="email"
                          placeholder={t('prelaunch.emailPlaceholder')}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          error={error}
                          required
                        />
                        <Button
                          type="submit"
                          variant="primary"
                          className="w-full"
                          isLoading={isSubmitting}
                          rightIcon={<ArrowRight className="w-4 h-4" />}
                        >
                          {t('prelaunch.submit')}
                        </Button>
                      </form>
                      <p className="pub-subtle text-xs mt-4">
                        {t('prelaunch.privacyNotice')}{' '}
                        <Link to="/politique-confidentialite" className="text-primary-600 hover:underline">
                          {tc('footer.privacyPolicy')}
                        </Link>
                        .
                      </p>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>

          {/* Hero Shot - Laptop on desktop, Phone on mobile */}
          <div className="mt-16 max-w-5xl mx-auto">
            {/* Desktop: laptop mockup with video */}
            <div className="hidden md:block">
              <LaptopMockup
                videoSrc="/videos/dashboard-demo.webm"
                alt={t('hero.mockupAlt', 'Aperçu du dashboard Splinity')}
              />
            </div>
            {/* Mobile: phone mockup with video */}
            <div className="md:hidden">
              <PhoneMockup
                type="video"
                videoSrc="/videos/dashboard-mobile-demo.webm"
                alt={t('hero.mockupAlt', 'Aperçu du dashboard Splinity')}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Fonctionnalités clés */}
      <section id="fonctionnalites" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="pub-title text-3xl font-bold mb-4">
              {t('features.sectionTitle')}
            </h2>
            <p className="pub-muted text-lg max-w-2xl mx-auto">
              {t('features.sectionSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature) => (
              <Link
                key={feature.title}
                to={feature.href}
                className="pub-card group p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="pub-ico w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="pub-title text-lg font-semibold mb-2 flex items-center gap-2">
                      {feature.title}
                      <ArrowRight className="w-4 h-4 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <p className="pub-muted">{feature.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Points forts */}
      <section className="pub-section-alt py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="pub-title text-3xl font-bold mb-4">
              {t('highlights.title')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {highlights.map((item) => (
              <div
                key={item.text}
                className="pub-card flex items-center gap-3 p-4"
              >
                <div className="pub-ico w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pub-cta py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="pub-title text-2xl md:text-3xl font-bold mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-lg opacity-90 mb-8">
            {t('cta.subtitle')}
          </p>

          {registrationEnabled === true ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button
                  variant="outline"
                  size="lg"
                  className="!bg-white !text-primary-700 !border-white hover:!bg-white/90"
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                >
                  {t('hero.createAccount')}
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  variant="outline"
                  size="lg"
                  className="!border-white/50 !text-white hover:!bg-white/10"
                >
                  {t('hero.signIn')}
                </Button>
              </Link>
            </div>
          ) : registrationEnabled === false ? (
            <div className="max-w-md mx-auto">
              {isSuccess ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <Check className="w-8 h-8 mx-auto mb-3" />
                  <p className="font-semibold mb-1">{t('prelaunch.thankYou')}</p>
                  <p className="text-sm opacity-80">
                    {t('prelaunch.contactSoonShort')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder={t('prelaunch.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={error}
                    required
                    className="!bg-white/10 !border-white/30 !text-white !placeholder-white/60"
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    className="!bg-white !text-primary-700 !border-white hover:!bg-white/90 whitespace-nowrap"
                    isLoading={isSubmitting}
                  >
                    {t('prelaunch.signUp')}
                  </Button>
                </form>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
